import type { DashboardLayout } from '../store/slices/dashboardPersonalizationSlice';

/**
 * Enhanced testing layouts for comprehensive dashboard testing
 * These layouts are optimized for different testing scenarios
 */
export const testingLayouts: DashboardLayout[] = [
  {
    id: 'testing_comprehensive',
    name: 'Testing Comprehensive',
    priority: 10,
    isDefault: false,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'AI Greeting',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'focus',
        type: 'focus',
        title: "Today's AI Focus",
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 1,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'detailed',
        },
      },
      {
        id: 'tips',
        type: 'tips',
        title: 'Smart Tips',
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 2,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'AI Recommendations',
        priority: 4,
        visible: true,
        collapsed: false,
        position: 3,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: true,
          maxItems: 4,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'progress',
        type: 'progress',
        title: 'Progress Insights',
        priority: 5,
        visible: true,
        collapsed: false,
        position: 4,
        loadingStrategy: 'lazy',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 3,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'card',
        },
      },
      {
        id: 'quick_actions',
        type: 'quick_actions',
        title: 'Smart Actions',
        priority: 6,
        visible: true,
        collapsed: false,
        position: 5,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 8,
          sortBy: 'engagement',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'achievements',
        type: 'achievements',
        title: 'Recent Achievements',
        priority: 7,
        visible: true,
        collapsed: false,
        position: 6,
        loadingStrategy: 'on_demand',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 3,
          sortBy: 'recency',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'social',
        type: 'social',
        title: 'Community Insights',
        priority: 8,
        visible: true,
        collapsed: false,
        position: 7,
        loadingStrategy: 'on_demand',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: true,
          showConfidenceIndicators: false,
          maxItems: 2,
          sortBy: 'engagement',
          filterBy: [],
          displayStyle: 'card',
        },
      },
    ],
  },
  {
    id: 'testing_minimal',
    name: 'Testing Minimal',
    priority: 11,
    isDefault: false,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'Welcome',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'quick_actions',
        type: 'quick_actions',
        title: 'Actions',
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 4,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'For You',
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'lazy',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 2,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
    ],
  },
  {
    id: 'testing_performance',
    name: 'Testing Performance',
    priority: 12,
    isDefault: false,
    sections: [
      {
        id: 'greeting',
        type: 'greeting',
        title: 'Quick Welcome',
        priority: 1,
        visible: true,
        collapsed: false,
        position: 0,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 1,
          sortBy: 'priority',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'focus',
        type: 'focus',
        title: 'Focus',
        priority: 2,
        visible: true,
        collapsed: false,
        position: 1,
        loadingStrategy: 'eager',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: true,
          maxItems: 1,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
      {
        id: 'recommendations',
        type: 'recommendations',
        title: 'Recommendations',
        priority: 3,
        visible: true,
        collapsed: false,
        position: 2,
        loadingStrategy: 'lazy',
        cacheStrategy: 'persistent',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: true,
          maxItems: 5,
          sortBy: 'ai_score',
          filterBy: [],
          displayStyle: 'list',
        },
      },
      {
        id: 'quick_actions',
        type: 'quick_actions',
        title: 'Actions',
        priority: 4,
        visible: true,
        collapsed: false,
        position: 3,
        loadingStrategy: 'eager',
        cacheStrategy: 'memory',
        customization: {
          showExplanations: false,
          showConfidenceIndicators: false,
          maxItems: 6,
          sortBy: 'engagement',
          filterBy: [],
          displayStyle: 'compact',
        },
      },
    ],
  },
];

/**
 * Get testing layout by ID
 */
export function getTestingLayout(layoutId: string): DashboardLayout | undefined {
  return testingLayouts.find(layout => layout.id === layoutId);
}

/**
 * Get all testing layout options for UI
 */
export function getTestingLayoutOptions() {
  return testingLayouts.map(layout => ({
    value: layout.id,
    label: layout.name,
  }));
}

/**
 * Check if a layout is a testing layout
 */
export function isTestingLayout(layoutId: string): boolean {
  return testingLayouts.some(layout => layout.id === layoutId);
}